import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AppDatePickerComponent } from 'src/app/core/components/app-date-picker/app-date-picker.component';
import { AppDateTimePickerComponent } from 'src/app/core/components/app-date-time-picker/app-date-time-picker.component';
import { AppHeightInputComponent } from 'src/app/core/components/app-height-input/height-input.component';
import { AppWeightInputComponent } from 'src/app/core/components/app-weight-input/weight-input.component';
import { NoNegativeInputDirective } from 'src/app/core/directives/noNegativeInput.directive';
import { PHONE_FORMAT_MESSAGE, phoneNumberValidator } from 'src/app/core/validators/phone.validator';
import { isSubmittable } from 'src/app/core/validators/form-submit';
import { TAKEN_ERROR, uniqueValueValidator } from 'src/app/core/validators/unique-value.validator';
import { MaterialModule } from 'src/app/core/material.module';
import { AvailabilityService } from 'src/app/core/services/availability.service';
import { ConversionBmiService } from 'src/app/core/services/conversion/conversion-bmi.service';
import { ConversionLengthService } from 'src/app/core/services/conversion/conversion-length.service';
import { ConversionWeightService } from 'src/app/core/services/conversion/conversion-weight.service';
import { PatientsService } from 'src/app/features/patients/services/patients.service';
import {
    ALL_TEST_LOCATION_OPTIONS,
    ALL_TEST_TYPES_OPTIONS,
    MAX_TESTS_PER_OPENING,
    MAX_TESTS_PER_OPENING_ERROR,
    PEDIATRIC_AGE_LIMIT,
    PEDIATRIC_DISABLED_WARNING,
    PEDIATRIC_INTERPRETATION_HINT,
    VALID_EMAIL_FORMAT,
    VALID_NAME_FORMAT,
} from 'src/app/features/tests/features/constants';

import { TestRoomSelectComponent } from 'src/app/features/test-rooms/components/test-rooms-select/test-rooms-select.component';
import { BOOLEAN_CHOOSE } from 'src/app/features/tests/features/constants';
import {
    calculateAge,
    getNextScheduleDateTime,
} from 'src/app/features/tests/features/services/helper';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { DepartmentSelectComponent } from 'src/app/features/departments/components/department-select/department-select.component';
import { UsersSelectComponent } from 'src/app/features/user-management/components/users-select/users-select.component';
import {
  ReportTemplate,
  isTitrationTemplate,
  TITRATION_TYPE_OPTIONS,
} from 'src/app/features/working-area/components/working-area2/types/signal-types';
import { PatientType } from 'src/app/features/dormotech-direct/dormotech-direct.types';


/**
 * What makes an email address acceptable on this form.
 *
 * Both checks, matching every other email field in the app. Angular's own
 * validator is the lenient one — it treats a bare `name@host` with no top-level
 * domain as valid — and the shared format is what insists on the dot and the
 * domain after it. The API applies the stricter rule of the two, so a field that
 * only ran the lenient check accepted addresses the save would then be refused
 * over, with the objection arriving as a failed request rather than as a message
 * under the field.
 *
 * Named once because the list is applied twice: the control declares it, and the
 * consent rule below rebuilds it from scratch to add or drop the required check.
 * Spelling it out in both places is how one of them came to be missing a
 * validator the other had.
 */
const EMAIL_VALIDATORS = [
  Validators.minLength(1),
  Validators.maxLength(256),
  Validators.email,
  Validators.pattern(VALID_EMAIL_FORMAT),
];

/** What makes a phone acceptable; a Direct patient's rule adds the required check to these. */
const PHONE_VALIDATORS = [Validators.minLength(1), Validators.maxLength(256), phoneNumberValidator];

@Component({
  selector: 'app-patient-main-info',
  templateUrl: './patient-main-info.component.html',
  styleUrls: ['./patient-main-info.component.scss'],
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    AppDateTimePickerComponent,
    AppDatePickerComponent,
    AppHeightInputComponent,
    AppWeightInputComponent,
    NoNegativeInputDirective,
    TestRoomSelectComponent,
    UsersSelectComponent,
    DepartmentSelectComponent,
  ],
})
export class PatientMainInfoComponent implements OnInit, OnChanges {
  @Output() mainFormValue = new EventEmitter();
  @Input() patientData: any;
  /**
   * Errors the API rejected the save with, keyed by control name.
   *
   * Applied to the controls so the offending field turns red and says why, rather
   * than leaving the user to match a toast against the form. Cleared as soon as
   * the field is edited, since the server's objection cannot outlive the value it
   * was about.
   */
  @Input() serverErrors: Record<string, string> | null = null;
  /**
   * The department the patient will belong to. Emitted on every change so callers
   * never have to fall back to the current user's department as a stand-in.
   */
  @Output() departmentSelected = new EventEmitter<string | null>();
  /** True when the user is scoped to one department and cannot pick another. */
  isDepartmentDisabled = false;

  /**
   * Whether this department may email patients the research consent (IRB) form.
   * Hides the option outright when it may not — the backend refuses the request
   * anyway, and offering a control that always fails is worse than no control.
   */
  @Input() researchConsentEnabled = false;
  /**
   * Whether the department may hand patients to Dormo Direct. Offers the Clinic /
   * Direct choice; without it every patient is a clinic patient.
   */
  readonly phoneFormatMessage = PHONE_FORMAT_MESSAGE;
  @Input() directEnabled = false;
  /** Why the department's Direct choice is not offered right now; shown in its place. */
  @Input() directUnavailable: string | null = null;
  /** Clinic or Direct, emitted on every change: the wizard's other steps follow it. */
  @Output() patientTypeChange = new EventEmitter<PatientType>();
  currDate = new Date();
  age: any;
  editable: boolean = false;
  allLocationOptions = ALL_TEST_LOCATION_OPTIONS;
  allTypeOptions = ALL_TEST_TYPES_OPTIONS;
  titrationTypeOptions = TITRATION_TYPE_OPTIONS;
  maxTestsPerOpening = MAX_TESTS_PER_OPENING;
  maxTestsPerOpeningError = MAX_TESTS_PER_OPENING_ERROR;

  /** Only a titration (a split night included) is run on a particular device, so only it asks which. */
  public get isTitration(): boolean {
    return isTitrationTemplate(this.patientDetailMainInfo.get('testType')?.value);
  }
  constructor(
    public patientService: PatientsService,
    public conversionBmiService: ConversionBmiService,
    public conversionLengthService: ConversionLengthService,
    public conversionWeightService: ConversionWeightService,
    public ability: AbilityService,
    private availabilityService: AvailabilityService
  ) {
    this.patientService.eventEmitter.subscribe((d) => {
      if (d == 'app-patient-main-info') {
        this.setData();
      }
      if (d == 'reset') {
        this.patientDetailMainInfo.reset();
      }
    });
  }

  ngOnInit(): void {
    // The department the PATIENT is being created in, published as soon as it is
    // known and on every change. Anything that depends on it — the research
    // consent option, for one — has to follow this rather than the signed-in
    // user's own department: an admin who works across departments creates
    // patients in departments they do not belong to.
    this.patientDetailMainInfo
      .get('departmentId')
      ?.valueChanges.subscribe((departmentId) => {
        this.departmentSelected.emit(departmentId || null);
      });

    if (!this.ability.isDepartmentVisible) {
      this.patientDetailMainInfo.patchValue({
        departmentId: this.ability.departmentId,
      });
      this.isDepartmentDisabled = true;
    }

    this.patientDetailMainInfo
      .get('patientType')
      ?.valueChanges.subscribe((patientType) => {
        this.applyResearchConsentEmailRule(!!this.patientDetailMainInfo.value.sendResearchConsent);
        this.applyDirectPhoneRule();
        if (patientType === 'DIRECT') {
          // Hidden for a Direct patient, who gets one test in no room: a value
          // left behind must not hold the step back unseen.
          const testsCount = this.patientDetailMainInfo.get('testsCount');
          if (testsCount?.enabled) {
            testsCount.setValue(1);
          }
          this.patientDetailMainInfo.get('roomId')?.setValue('');
        }
        this.patientTypeChange.emit(patientType);
      });

    this.watchPatientIdAvailability();
    this.clearServerErrorOnEdit();
    this.patientDetailMainInfo
      .get('sendResearchConsent')
      ?.valueChanges.subscribe((checked) => {
        this.applyResearchConsentEmailRule(!!checked);
      });
    this.patientDetailMainInfo
      .get('dob')
      ?.valueChanges.subscribe((dobValue) => {
        this.age = calculateAge(dobValue);
        this.applyPediatricDefault(dobValue);
      });
    this.conversionLengthService.setPatientValidators(
      this.patientDetailMainInfo,
      'height'
    );
    this.conversionWeightService.setPatientValidators(
      this.patientDetailMainInfo,
      'weight'
    );
    this.patientDetailMainInfo
    .get('testType')
    ?.valueChanges.subscribe((testType) => {
      this.applyTestTypeDefaults(testType);

      const testsCountControl =
        this.patientDetailMainInfo.get('testsCount');

      if (!testsCountControl) return;

      if (testType === ReportTemplate.MSLT) {
        testsCountControl.setValue(6, { emitEvent: false });
        testsCountControl.disable({ emitEvent: false });
      } else {
        testsCountControl.enable({ emitEvent: false });
        testsCountControl.setValue(1, { emitEvent: false });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // A department without Dormo Direct makes clinic patients only.
    if (!this.directEnabled && this.isDirect) {
      this.patientDetailMainInfo.get('patientType')?.setValue('CLINIC');
    }
    // A department that has since been opted out must not carry a tick forward
    // into the payload, where the backend would refuse the whole save.
    if (!this.researchConsentEnabled) {
      this.patientDetailMainInfo
        .get('sendResearchConsent')
        ?.setValue(false, { emitEvent: false });
      this.applyResearchConsentEmailRule(false);
    }
    // Re-hydrate only when the patient being shown actually changes. Any other
    // input arriving mid-wizard — the research consent flag resolves once a
    // department is picked on a later step — would otherwise patch the saved
    // patient back over this step and wipe everything typed so far.
    if (changes['patientData']) {
      this.setFormValue();
    }
    // Guarded the same way, so a rejection is applied when it arrives and not
    // re-applied on every unrelated input change.
    if (changes['serverErrors']) {
      this.applyServerErrors();
    }
  }

  /**
   * The consent form is delivered by email and nothing else, so ticking the box
   * makes the address mandatory. Enforced on the form rather than left to the
   * server: the wizard must refuse to advance past this step, which is what the
   * ticket means by not allowing the user to proceed.
   */
  private applyResearchConsentEmailRule(required: boolean) {
    const email = this.patientDetailMainInfo.get('email');
    if (!email) {
      return;
    }

    // A Direct patient needs one too: Dormotech reaches the patient by it.
    email.setValidators(
      required || this.isDirect ? [...EMAIL_VALIDATORS, Validators.required] : [...EMAIL_VALIDATORS]
    );
    email.updateValueAndValidity({ emitEvent: false });
  }

  /** A Direct patient needs a phone: the courier delivering the device calls it. */
  private applyDirectPhoneRule() {
    const phone = this.patientDetailMainInfo.get('phone');
    if (!phone) {
      return;
    }
    phone.setValidators(
      this.isDirect ? [...PHONE_VALIDATORS, Validators.required] : [...PHONE_VALIDATORS]
    );
    phone.updateValueAndValidity({ emitEvent: false });
  }

  /** Whether the patient is being created as a Direct patient. */
  get isDirect(): boolean {
    return this.patientDetailMainInfo?.get('patientType')?.value === 'DIRECT';
  }

  setPatientType(patientType: PatientType) {
    this.patientDetailMainInfo.get('patientType')?.setValue(patientType);
  }

  public patientDetailMainInfo = new FormGroup({
    departmentId: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    firstName: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
      Validators.required,
      Validators.pattern(VALID_NAME_FORMAT),
    ]),
    middleName: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
      Validators.pattern(VALID_NAME_FORMAT),
    ]),
    lastName: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
      Validators.required,
      Validators.pattern(VALID_NAME_FORMAT),
    ]),
    patientId: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
      Validators.required,
    ]),
    email: new FormControl('', [...EMAIL_VALIDATORS]),
    phone: new FormControl('', [...PHONE_VALIDATORS]),
    dob: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    age: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
      Validators.pattern('^[0-9]*$'),
    ]),
    gender: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    weight: new FormControl(''),
    height: new FormControl(''),
    isActive: new FormControl(true, [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    bmi: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    hasExternalFile: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    isPediatric: new FormControl(false),
    sendResearchConsent: new FormControl(false),
    // Kept on reset: a new patient starts as a clinic one.
    patientType: new FormControl<PatientType>('CLINIC', { nonNullable: true }),
    titrationType: new FormControl(''),
    testType: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    roomId: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),

    testsCount: new FormControl(1, [
      Validators.min(1),
      Validators.max(MAX_TESTS_PER_OPENING),
    ]),

    location: new FormControl('Lab', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    scheduleDateTime: new FormControl('', [Validators.required]),
    interpretingPhysicianId: new FormControl(''),
  });

  active = [
    { value: true, viewValue: 'Active' },
    { value: false, viewValue: 'Inactive' },
  ];
  gender = [
    { viewValue: 'Male', value: 'MALE' },
    { viewValue: 'Female', value: 'FEMALE' },
  ];

  boolChoose = BOOLEAN_CHOOSE;
  bmi = 0;

  /** Set once the user toggles the checkbox, so DOB edits stop overriding them. */
  private pediatricTouched = false;
  /** True while setFormValue patches the form from the saved patient record. */
  private hydrating = false;

  // : minors default to pediatric interpretation, but the choice stays
  // the user's — an explicit toggle is never undone by a later DOB change, and
  // the value saved on the patient always wins over the age-derived default.
  private applyPediatricDefault(dobValue: string) {
    if (this.hydrating || this.pediatricTouched || !dobValue) {
      return;
    }
    this.patientDetailMainInfo
      .get('isPediatric')
      ?.setValue(calculateAge(dobValue) < PEDIATRIC_AGE_LIMIT, {
        emitEvent: false,
      });
  }

  onPediatricChange() {
    this.pediatricTouched = true;
  }

  pediatricHint = PEDIATRIC_INTERPRETATION_HINT;
  pediatricWarning = PEDIATRIC_DISABLED_WARNING;

  /**
   * True for a minor left on the adult ruleset. Age alone is not enough: an
   * empty DOB reads as age 0, which would warn about every untouched form.
   */
  get pediatricWarningVisible(): boolean {
    const dob = this.patientDetailMainInfo.value.dob;
    return (
      !!dob &&
      calculateAge(dob) < PEDIATRIC_AGE_LIMIT &&
      !this.patientDetailMainInfo.value.isPediatric
    );
  }

  /**
   * A home sleep test is by definition recorded at home, while every other study
   * type is run in the lab, so picking the study type moves the location with it
   * instead of leaving the previous pick behind. It is only a default: the
   * location stays editable, and a later manual change is never undone unless
   * the study type itself changes again.
   */
  private applyTestTypeDefaults(testType?: string | null) {
    this.patientDetailMainInfo
      .get('location')
      ?.setValue(testType === ReportTemplate.HST_TYPE_2 ? 'Home' : 'Lab', {
        emitEvent: false,
      });
  }

  setData() {
    this.validateAllFormFields(this.patientDetailMainInfo);
    // A duplicate check still waiting on the server counts as passing: the save
    // asks the same question again, and blocking here would answer a click made a
    // moment too early with "fill all required fields" on a form that is fine.
    const valid = isSubmittable(this.patientDetailMainInfo);
    const value = {
      ...this.patientDetailMainInfo.value,
      weight: this.patientDetailMainInfo.value.weight
        ? Number(this.patientDetailMainInfo.value.weight)
        : null,
      height: this.patientDetailMainInfo.value.height
        ? Number(this.patientDetailMainInfo.value.height)
        : null,
    };
    this.mainFormValue.emit({
      form: 'app-patient-main-info',
      valid: valid,
      value: value,
    });
  }
  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  setFormValue() {
    const dob = this.patientData?.birthdayDate?.includes('T')
      ? this.patientData?.birthdayDate.split('T')[0]
      : this.patientData?.birthdayDate;
    const scheduleDateTime = getNextScheduleDateTime();
    this.hydrating = true;
    this.patientDetailMainInfo.patchValue({
      firstName: this.patientData?.user?.firstName,
      middleName:
        this.patientData?.middleName == null
          ? ''
          : this.patientData?.middleName,
      lastName: this.patientData?.user?.lastName,
      patientId: this.patientData?.patientId,
      email: this.patientData?.user?.email,
      phone: this.patientData?.user?.phone,
      roomId: this.patientData?.roomId,
      dob: dob,
      // Age is derived from the date of birth against the viewer's own calendar
      // rather than taken from the server's figure. The server measures elapsed
      // time from UTC midnight, so for a viewer ahead of UTC the age it reports
      // is still a year behind for the whole of the patient's birthday.
      age: dob ? String(calculateAge(dob)) : '',
      bmi: this.patientData?.bmi == null ? '' : this.patientData?.bmi,
      gender: this.patientData?.gender,
      weight:
        this.patientData?.weight == 0 ? '' : String(this.patientData?.weight),
      height:
        this.patientData?.height == 0 ? '' : String(this.patientData?.height),
      isActive: this.patientData?.user?.isActive,
      testsCount: this.patientData?.testsCount || 1,
      hasExternalFile: this.patientData?.hasExternalFile,
      isPediatric: !!this.patientData?.isPediatric,
      scheduleDateTime: scheduleDateTime,
      interpretingPhysicianId: this.patientData?.interpretingPhysicianId,
      departmentId: !this.patientData?.user?.departmentId
        ? ''
        : this.patientData?.user?.departmentId,
    });
    this.hydrating = false;
    // A brand-new patient has no saved flag, so derive it from the DOB that
    // was just patched in.
    if (!this.patientData?.id) {
      this.applyPediatricDefault(this.patientDetailMainInfo.value.dob);
    }
  }

  onHeightChange(value) {
    this.patientDetailMainInfo.controls['height'].setValue(value);
    this.calculateBmi();
  }

  onWeightChange(value) {
    this.patientDetailMainInfo.controls['weight'].setValue(value);
    this.calculateBmi();
  }

  calculateBmi() {
    const height = Number(this.patientDetailMainInfo.value.height);
    const weight = Number(this.patientDetailMainInfo.value.weight);
    this.bmi = this.conversionBmiService.calculateBmi(height, weight);
  }

  onTestRoomChange(value: string) {
    this.patientDetailMainInfo.get('roomId')?.setValue(value);
  }

  onDepartmentChange(value: string) {
    this.patientDetailMainInfo.get('departmentId')?.setValue(value);
  }

  onInterpretingPhysicianIdUserChange(value: string) {
    this.patientDetailMainInfo.get('interpretingPhysicianId')?.setValue(value);
  }

  /** True while the API's objection about this field still stands. */
  public hasServerError(control: string): boolean {
    return !!this.patientDetailMainInfo.get(control)?.errors?.['server'];
  }

  public serverErrorFor(control: string): string {
    return this.patientDetailMainInfo.get(control)?.errors?.['server'] ?? '';
  }

  /** The value the API refused, per control, so a re-hydration is not mistaken
   * for the user fixing it. */
  private rejectedValues = new Map<string, unknown>();

  /** Push server-rejected errors onto their controls. */
  private applyServerErrors() {
    Object.entries(this.serverErrors ?? {}).forEach(([name, message]) => {
      const control = this.patientDetailMainInfo.get(name);
      if (!control) {
        return;
      }
      this.rejectedValues.set(name, control.value);
      control.setErrors({ ...(control.errors ?? {}), server: message });
      control.markAsTouched();
    });
  }

  /**
   * A server error is about one particular value, so the moment that value changes
   * the objection is stale and has to go — otherwise the field stays red while the
   * user looks at the corrected value, and the form refuses to submit.
   *
   * Compared against the refused value rather than just reacting to any emission:
   * re-hydrating the form patches every control, and treating that as the user
   * typing wiped the error in the same tick it was set.
   */
  private clearServerErrorOnEdit() {
    Object.keys(this.patientDetailMainInfo.controls).forEach((name) => {
      const control = this.patientDetailMainInfo.get(name);
      control?.valueChanges.subscribe((value) => {
        if (!control.errors?.['server']) {
          return;
        }
        if (this.rejectedValues.get(name) === value) {
          return;
        }
        this.rejectedValues.delete(name);
        const { server, ...rest } = control.errors as Record<string, unknown>;
        control.setErrors(Object.keys(rest).length ? rest : null);
        // Re-run the control's own validators, which the server error masked.
        control.updateValueAndValidity({ emitEvent: false });
      });
    });
  }
  /**
   * Asks the server, as the ID is typed, whether another patient already holds it
   * — so the clash is shown next to the field instead of arriving as a rejected
   * save at the end of the wizard.
   *
   * `patientData` is populated when this form opens over an existing record, so
   * that record is excluded and its own saved ID is never asked about. On a
   * genuinely new patient both are absent and every value is asked about, which is
   * what a new patient needs.
   */
  private watchPatientIdAvailability() {
    this.patientDetailMainInfo
      .get('patientId')
      ?.setAsyncValidators(
        uniqueValueValidator({
          check: (value) =>
            this.availabilityService.isPatientIdAvailable(
              value,
              this.patientData?.id
            ),
          currentValue: () => this.patientData?.patientId,
        })
      );
  }

  /** True while the server is being asked about the value in this field. */
  public isChecking(control: string): boolean {
    return !!this.patientDetailMainInfo.get(control)?.pending;
  }

  /** True once the server has said another record holds this value. */
  public isTaken(control: string): boolean {
    return !!this.patientDetailMainInfo.get(control)?.errors?.[TAKEN_ERROR];
  }

}
